import React from 'react';
import { Link } from 'gatsby';
import { storyblokEditable, useStoryblokState } from 'gatsby-source-storyblok';
import { useLocation } from '@reach/router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core';
import StoryblokClient from 'storyblok-js-client';
import { extractLanguage, getStoryblokAlt } from '../../helpers/common';

const storyblokClient = new StoryblokClient({
  accessToken: process.env.GATSBY_STORYBLOK_ACCESS_TOKEN,
  cache: {
    clear: 'auto',
    type: 'memory',
  },
});

const getStoryblokDimensions = (url) => {
  const [rawWidth, rawHeight] = url?.split('/')[5]?.split('x') || [];
  const width = Number.parseInt(rawWidth, 10);
  const height = Number.parseInt(rawHeight, 10);

  return {
    width: Number.isNaN(width) ? undefined : width,
    height: Number.isNaN(height) ? undefined : height,
  };
};

function Post({ post }) {
  const storyBlokPost = useStoryblokState(post, {
    resolveRelations: [
      'blogpost.author'],
  });
  const location = useLocation();

  const language = extractLanguage(location.pathname);

  const { content } = storyBlokPost;
  const translatedSlug = storyBlokPost.translated_slugs?.length
    ? storyBlokPost.translated_slugs[0] : undefined;
  const title = translatedSlug && language === translatedSlug.lang
    ? translatedSlug.name : content?.title
  || storyBlokPost.name || storyBlokPost.slug;
  const featuredImg = content?.hero?.filename || '';
  const {
    width: featuredImgWidth,
    height: featuredImgHeight,
  } = getStoryblokDimensions(featuredImg);
  return (
    <Link
      key={storyBlokPost.uuid}
      to={`/${storyBlokPost.full_slug}`}
      className="mx-auto aspect-[134/85] w-full relative group overflow-hidden"
      aria-label={`${title} - Read Blog Post`}
    >
      <article
        itemScope
        itemType="http://schema.org/Article"
        className="bg-slate-100 dark:bg-latori-green-dark shadow-lg h-full"
      >
        {featuredImg
          && (
          <picture>
            <source
              type="image/avif"
              srcSet={`${featuredImg}/m/300x0/filters:format(avif) 300w, ${featuredImg}/m/450x0/filters:format(avif) 450w, ${featuredImg}/m/600x0/filters:format(avif) 600w, ${featuredImg}/m/800x0/filters:format(avif) 800w, ${featuredImg}/m/1000x0/filters:format(avif) 1000w`}
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
            />
            <img
              src={`${featuredImg}/m/450x0/filters:format(webp)`}
              srcSet={`${featuredImg}/m/300x0/filters:format(webp) 300w, ${featuredImg}/m/450x0/filters:format(webp) 450w, ${featuredImg}/m/600x0/filters:format(webp) 600w, ${featuredImg}/m/800x0/filters:format(webp) 800w, ${featuredImg}/m/1000x0/filters:format(webp) 1000w`}
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
              alt={getStoryblokAlt(content?.hero, '')}
              width={featuredImgWidth}
              height={featuredImgHeight}
              className="w-full h-full object-cover absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"
              loading="lazy"
            />
          </picture>
          )}
        <div className="bg-latori-green-dark opacity-90 w-full h-full absolute bottom-0 left-0 transition-[height] group-hover:h-[0%]" />
        <div className="w-full h-full absolute top-0 left-0 flex flex-col justify-center items-start px-4 group-hover:opacity-90 transition-opacity text-white">
          <h2 className="text-2xl text-white drop-shadow-lg" itemProp="headline">{title}</h2>
        </div>
      </article>
    </Link>
  );
}

function BlogGridView({ blok }) {
  const { posts: rawPosts = [] } = blok;
  const location = useLocation();
  const sectionRef = React.useRef(null);

  const currentLanguage = extractLanguage(location.pathname);
  const [resolvedPosts, setResolvedPosts] = React.useState(/** @type {any[]} */ ([]));
  const [shouldResolvePosts, setShouldResolvePosts] = React.useState(false);

  React.useEffect(() => {
    if (!sectionRef.current || typeof IntersectionObserver === 'undefined') {
      setShouldResolvePosts(true);
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setShouldResolvePosts(true);
      observer.disconnect();
    }, { rootMargin: '600px 0px' });

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const shouldResolveUuidPosts = Array.isArray(rawPosts)
      && rawPosts.length > 0
      && typeof rawPosts[0] === 'string'
      && shouldResolvePosts
      && process.env.GATSBY_STORYBLOK_ACCESS_TOKEN;

    if (!shouldResolveUuidPosts) {
      setResolvedPosts([]);
    }

    const fetchPostsByUuid = async () => {
      try {
        const { data } = await storyblokClient.get('cdn/stories', {
          by_uuids: rawPosts.join(','),
          language: currentLanguage,
          version: 'published',
          per_page: rawPosts.length,
        });

        if (cancelled) {
          return;
        }

        const storyByUuid = new Map((data?.stories || [])
          .map((story) => [story.uuid, story]));
        const orderedStories = rawPosts
          .map((uuid) => storyByUuid.get(uuid))
          .filter(Boolean)
          .map((story) => ({
            ...story,
            content: story?.content || {},
          }));

        setResolvedPosts(orderedStories);
      } catch (error) {
        if (!cancelled) {
          setResolvedPosts([]);
        }
      }
    };

    if (shouldResolveUuidPosts) {
      fetchPostsByUuid();
    }

    return () => {
      cancelled = true;
    };
  }, [currentLanguage, rawPosts, shouldResolvePosts]);

  const posts = React.useMemo(() => {
    if (!Array.isArray(rawPosts) || rawPosts.length === 0) {
      return [];
    }

    if (Object.prototype.hasOwnProperty.call(rawPosts[0], 'node')) {
      return rawPosts
        .map(({ node }) => {
          if (node.content && typeof node.content === 'string') {
            try {
              return {
                ...node,
                content: JSON.parse(node.content),
              };
            } catch (error) {
              return node;
            }
          }

          return node;
        })
        .filter(({ lang }) => lang === currentLanguage);
    }

    if (typeof rawPosts[0] === 'string') {
      return resolvedPosts;
    }

    return rawPosts.filter((entry) => entry?.lang === currentLanguage);
  }, [currentLanguage, rawPosts, resolvedPosts]);

  return (
    <div ref={sectionRef} {...storyblokEditable(blok)} className="px-4 lg:px-16 pb-8 lg:pb-32">
      <div className="mt-[60px] lg:mt-20 grid grid-cols-1 xl:grid-cols-3 md:grid-cols-2 gap-2 md:gap-4" id="blog-post-list">
        {posts.map((entry) => (
          <Post key={entry.uuid} post={entry} />
        ))}
        {blok?.show_more_tile && (
        <Link
          to={`${currentLanguage === 'default' ? '' : '/en'}/blog`}
          className="flex bg-green-800 hover:bg-green-900 text-white group w-full h-full py-12"
        >
          <div className="flex m-auto items-center group-hover:scale-105 transition-transform">
            <span className="mr-2">
              {currentLanguage === 'default' ? 'Mehr' : 'More'}
              <span className="sr-only">
                {currentLanguage === 'en' ? ' blog posts' : ' Blogbeiträge'}
              </span>
            </span>
            <FontAwesomeIcon
              icon={icon({ prefix: 'far', iconName: 'arrow-right' })}
              size="2x"
            />
          </div>
        </Link>
        )}
      </div>
    </div>
  );
}

export default BlogGridView;
